import React, { useState, useEffect, useRef } from 'react';
import CardExporter from './CardExporter';
import './DigitalCard.css';

const DigitalCard = ({ 
  userData, 
  size = 'medium', 
  interactive = true,
  showFullDetail = false 
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isFlipped, setIsFlipped] = useState(false);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [isExporting, setIsExporting] = useState(false);
  const cardRef = useRef(null);
  
  if (!userData) {
    return (
      <div className="digital-card-skeleton">
        <div className="skeleton-shimmer"></div>
      </div>
    );
  }
  
  const {
    displayName,
    title,
    bio,
    cardImage,
    curationSignature = {},
    email,
    userId,
    forwardingEmail
  } = userData;
  
  // Advanced preference analysis
  const analyzePreferences = () => {
    const weights = curationSignature.signalWeights || {};
    const totalSignals = Object.keys(weights).length;
    
    // Get top positive signals
    const topSignals = Object.entries(weights)
      .filter(([_, weight]) => parseInt(weight) > 0)
      .sort((a, b) => parseInt(b[1]) - parseInt(a[1]))
      .slice(0, 8);
    
    // Calculate aesthetic intensity
    const totalWeight = topSignals.reduce((sum, [_, weight]) => sum + parseInt(weight), 0);
    const intensity = totalSignals > 0 ? Math.min(totalWeight / (totalSignals * 5), 1) : 0;
    
    // Determine dominant aesthetic
    const styleSignals = topSignals.filter(([signal]) => 
      ['minimal', 'brutalist', 'modern', 'classical', 'industrial'].some(style => 
        signal.toLowerCase().includes(style)
      )
    );
    
    const dominantStyle = styleSignals[0]?.[0] || 'eclectic';
    
    return {
      topSignals,
      intensity,
      dominantStyle: dominantStyle.toLowerCase(),
      diversity: totalSignals,
      focusScore: topSignals.length > 0 ? (parseInt(topSignals[0][1]) / 5) : 0
    };
  };
  
  const preferences = analyzePreferences();
  
  // Dynamic background based on aesthetic analysis
  const generateSmartBackground = () => {
    if (cardImage?.startsWith('linear-gradient') || cardImage?.startsWith('radial-gradient')) {
      return { background: cardImage };
    }
    
    if (cardImage?.startsWith('http') || cardImage?.startsWith('url(')) {
      return { 
        backgroundImage: cardImage.startsWith('url(') ? cardImage : `url(${cardImage})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      };
    }
    
    // AI-style background generation based on preferences
    const { dominantStyle, intensity, focusScore } = preferences;
    
    const backgroundMap = {
      minimal: {
        gradient: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 50%, #cbd5e1 100%)',
        overlay: 'rgba(255, 255, 255, 0.1)'
      },
      brutalist: {
        gradient: 'linear-gradient(135deg, #1e293b 0%, #334155 50%, #475569 100%)',
        overlay: 'rgba(0, 0, 0, 0.2)'
      },
      modern: {
        gradient: 'linear-gradient(135deg, #0f172a 0%, #1e40af 50%, #3b82f6 100%)',
        overlay: 'rgba(59, 130, 246, 0.1)'
      },
      classical: {
        gradient: 'linear-gradient(135deg, #78716c 0%, #a8a29e 50%, #d6d3d1 100%)',
        overlay: 'rgba(120, 113, 108, 0.1)'
      },
      industrial: {
        gradient: 'linear-gradient(135deg, #27272a 0%, #52525b 50%, #71717a 100%)',
        overlay: 'rgba(82, 82, 91, 0.15)'
      },
      eclectic: {
        gradient: 'linear-gradient(135deg, #ec4899 0%, #8b5cf6 50%, #06b6d4 100%)',
        overlay: 'rgba(139, 92, 246, 0.1)'
      }
    };
    
    const style = backgroundMap[dominantStyle] || backgroundMap.eclectic;
    
    return {
      background: style.gradient,
      '--card-overlay-color': style.overlay,
      '--card-intensity': intensity,
      '--card-focus': focusScore
    };
  };
  
  // Mouse tracking for 3D effects
  const handleMouseMove = (e) => {
    if (!cardRef.current) return;
    
    const rect = cardRef.current.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width;
    const y = (e.clientY - rect.top) / rect.height;
    
    setMousePosition({ x, y });
  };
  
  // Calculate 3D transform
  const getTransform = () => {
    if (!isHovered) return '';
    
    const { x, y } = mousePosition;
    const rotateX = (y - 0.5) * -4.6; // Reduced intensity
    const rotateY = (x - 0.5) * 4.6;
    
    return `perspective(462.4px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) translateZ(4.6px)`;
  };
  
  // Export functionality
  const handleExport = async () => {
    setIsExporting(true);
    
    // Add micro-delay for UX feedback
    await new Promise(resolve => setTimeout(resolve, 300));
    
    const success = CardExporter.exportToVCF({
      name: displayName || 'Artifex User',
      email,
      forwardingEmail,
      title,
      bio,
      username: email?.split('@')[0] || userId,
      userId,
      curationSignature,
      visibilityMode: userData.visibilityMode
    });
    
    setIsExporting(false);
    
    if (success) {
      // Visual success feedback
      const card = cardRef.current;
      if (card) {
        card.style.transform = 'scale(1.02)';
        setTimeout(() => {
          card.style.transform = '';
        }, 200);
      }
    }
  };
  
  // Share functionality
  const handleShare = async () => {
    const profileUrl = `https://www.a-r-t-i-f-e-x.com/profiles/${email?.split('@')[0] || userId}`;
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: `${displayName}'s Aesthetic Profile`,
          text: `Discover ${displayName}'s curated aesthetic on Artifex`,
          url: profileUrl
        });
      } catch (error) {
        // User cancelled
      }
    } else {
      await navigator.clipboard.writeText(profileUrl);
      // Could add toast notification here
    }
  };
  
  return (
    <div className={`digital-card ${size} ${preferences.dominantStyle}`}>
      <div 
        ref={cardRef}
        className={`card-container ${isHovered ? 'hovered' : ''} ${isFlipped ? 'flipped' : ''}`}
        style={{
          transform: getTransform(),
          ...generateSmartBackground()
        }}
        onMouseMove={handleMouseMove}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => {
          setIsHovered(false);
          setMousePosition({ x: 0.5, y: 0.5 });
        }}
      >
        {/* Front of card */}
        <div className="card-face card-front">
          {/* Dynamic mesh gradient overlay */}
          <div className="card-mesh-overlay"></div>
          
          {/* Main content */}
          <div className="card-content">
            {/* Header with smart layout */}
            <div className="card-header">
              <div className="identity-section">
                <h1 className="card-name">{displayName}</h1>
                {title && <p className="card-title">{title}</p>}
              </div>
              
              {/* Aesthetic intensity indicator */}
              <div className="intensity-indicator">
                <div 
                  className="intensity-bar"
                  style={{ '--intensity': preferences.intensity }}
                ></div>
                <span className="intensity-label">
                  {preferences.intensity > 0.8 ? 'Focused' : 
                   preferences.intensity > 0.5 ? 'Defined' : 'Exploring'}
                </span>
              </div>
            </div>
            
            {/* Bio with smart truncation */}
            {bio && (
              <div className="card-bio">
                <p>{bio}</p>
              </div>
            )}
            
            {/* Smart preference visualization */}
            {preferences.topSignals.length > 0 && (
              <div className="preferences-section">
                <div className="preference-grid">
                  {preferences.topSignals.map(([signal, weight], index) => (
                    <div 
                      key={signal}
                      className={`preference-pill weight-${weight}`}
                      style={{
                        '--delay': `${index * 50}ms`,
                        '--weight-opacity': Math.min(parseInt(weight) / 5, 1)
                      }}
                    >
                      <span className="signal-text">{signal}</span>
                      <div className="weight-indicator">
                        {Array.from({ length: parseInt(weight) }, (_, i) => (
                          <div key={i} className="weight-dot"></div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
                
                {/* Aesthetic summary */}
                <div className="aesthetic-summary">
                  <span className="dominant-style">{preferences.dominantStyle}</span>
                  <span className="diversity-indicator">
                    {preferences.diversity} signals • {Math.round(preferences.intensity * 100)}% focused
                  </span>
                </div>
              </div>
            )}
            
            {/* Privacy indicator */}
            {forwardingEmail && (
              <div className="privacy-badge">
                <div className="privacy-icon"></div>
                <span>Privacy Protected</span>
              </div>
            )}
          </div>
          
          {/* Interactive actions */}
          {interactive && (
            <div className="card-actions">
              <button 
                className="action-button primary"
                onClick={handleExport}
                disabled={isExporting}
              >
                <div className="button-content">
                  {isExporting ? (
                    <>
                      <div className="spinner"></div>
                      <span>Exporting...</span>
                    </>
                  ) : (
                    <>
                      <div className="button-icon">⤓</div>
                      <span>Export Card</span>
                    </>
                  )}
                </div>
              </button>
              
              <button 
                className="action-button secondary"
                onClick={handleShare}
              >
                <div className="button-content">
                  <div className="button-icon">↗</div>
                  <span>Share</span>
                </div>
              </button>
              
              {showFullDetail && (
                <button 
                  className="action-button tertiary"
                  onClick={() => setIsFlipped(!isFlipped)}
                >
                  <div className="button-content">
                    <div className="button-icon">⟲</div>
                    <span>Details</span>
                  </div>
                </button>
              )}
            </div>
          )}
        </div>
        
        {/* Back of card (detailed view) */}
        {showFullDetail && (
          <div className="card-face card-back">
            <div className="card-content">
              <div className="detail-header">
                <h2>Aesthetic Profile</h2>
                <button 
                  className="flip-back-button"
                  onClick={() => setIsFlipped(false)}
                >
                  ←
                </button>
              </div>
              
              {/* Detailed preference breakdown */}
              <div className="preference-breakdown">
                {Object.entries(curationSignature.signalWeights || {}).map(([signal, weight]) => (
                  <div key={signal} className="signal-detail">
                    <span className="signal-name">{signal}</span>
                    <div className="weight-scale">
                      <div 
                        className="weight-fill"
                        style={{ '--weight': Math.abs(parseInt(weight)) / 5 }}
                      ></div>
                    </div>
                    <span className="weight-value">{weight > 0 ? '+' : ''}{weight}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DigitalCard;